import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import {
  Users,
  Search,
  Plus,
  Pencil,
  ArrowLeft,
  Shield,
  Dumbbell,
  AlertTriangle,
  X,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import InviteUserDialog from "./InviteUserDialog";
import EditUserDialog from "./EditUserDialog";
import {
  getMembershipConfig,
  formatMembership,
  normaliseTier,
} from "@/lib/membershipUtils";

const FILTER_PILLS = [
  { id: "all", label: "All" },
  { id: "active", label: "Active" },
  { id: "expired", label: "Expired" },
  { id: "elite_coaching", label: "Elite Coaching" },
  { id: "strength", label: "Strength" },
  { id: "foundation", label: "Foundation" },
];

export default function AdminMembersSection({
  onBack,
  initialFilter = "all",
}) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState(initialFilter);
  const [showInvite, setShowInvite] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  const { data: members = [], isLoading } = useQuery({
    queryKey: ["admin_members"],
    queryFn: () => base44.entities.User.list(),
  });

  const filteredMembers = useMemo(() => {
    return members.filter((member) => {
      // Search
      const q = search.toLowerCase().trim();
      const matchSearch =
        !q ||
        (member.full_name && member.full_name.toLowerCase().includes(q)) ||
        (member.email && member.email.toLowerCase().includes(q));

      if (!matchSearch) return false;

      // Filter
      const isExpired =
        member.membership_expires && new Date(member.membership_expires) < new Date();

      if (filter === "all") return true;
      if (filter === "active") return !isExpired;
      if (filter === "expired") return isExpired;
      if (filter === "elite_coaching") {
        return normaliseTier(member.membership_type) === "elite_coaching";
      }
      if (filter === "strength") {
        return normaliseTier(member.membership_type) === "strength";
      }
      if (filter === "foundation") {
        return normaliseTier(member.membership_type) === "foundation";
      }
      return true;
    });
  }, [members, search, filter]);

  return (
    <div className="space-y-4 pb-20">
      {/* Top Bar with Back navigation & Title */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onBack}
            className="p-1.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300 hover:text-white transition-all cursor-pointer"
            aria-label="Back to Command Center"
          >
            <ArrowLeft className="w-4 h-4 text-sky-400" />
          </button>
          <div>
            <h1 className="text-lg font-bold text-white leading-tight">Members</h1>
            <p className="text-[11px] text-slate-400">
              {filteredMembers.length} of {members.length} members
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={() => setShowInvite(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-sky-500 hover:bg-sky-400 text-slate-950 font-bold text-xs shadow-md active:scale-95 transition-all cursor-pointer"
          data-testid="invite-member-btn"
        >
          <Plus className="w-3.5 h-3.5" />
          <span>Invite</span>
        </button>
      </div>

      {/* Search Input */}
      <div className="relative">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or email…"
          className="w-full pl-9 pr-8 py-2 rounded-xl bg-[#071326] border border-white/10 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-sky-400"
        />
        {search && (
          <button
            type="button"
            onClick={() => setSearch("")}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Status Filter Horizontal Pills */}
      <div className="flex items-center gap-1.5 overflow-x-auto pb-1 no-scrollbar">
        {FILTER_PILLS.map((pill) => {
          const isActive = filter === pill.id;
          return (
            <button
              key={pill.id}
              type="button"
              onClick={() => setFilter(pill.id)}
              className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap transition-all cursor-pointer ${
                isActive
                  ? "bg-sky-500 text-slate-950 shadow-sm"
                  : "bg-white/5 text-slate-400 hover:text-white border border-white/5"
              }`}
            >
              {pill.label}
            </button>
          );
        })}
      </div>

      {/* Member Cards List */}
      {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-20 rounded-2xl bg-white/5 animate-pulse" />
          ))}
        </div>
      ) : filteredMembers.length === 0 ? (
        <div className="rounded-2xl p-8 bg-[#071326] border border-white/10 text-center space-y-2">
          <Users className="w-8 h-8 text-slate-500 mx-auto" />
          <p className="text-xs font-bold text-white">No members found</p>
          <p className="text-[11px] text-slate-400">
            {search ? "Try refining your search terms." : "No records match the active filter."}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {filteredMembers.map((member) => {
            const isExpired =
              member.membership_expires && new Date(member.membership_expires) < new Date();
            const tierConfig = getMembershipConfig(member.membership_type);
            const initials = member.full_name
              ? member.full_name
                  .split(" ")
                  .map((n) => n[0])
                  .join("")
                  .toUpperCase()
                  .slice(0, 2)
              : (member.email?.[0] || "?").toUpperCase();

            return (
              <div
                key={member.id}
                className="p-3 rounded-2xl bg-gradient-to-r from-[#08152B] to-[#040C1A] border border-white/10 hover:border-sky-400/40 transition-all flex items-center justify-between gap-3 shadow-sm"
                data-testid={`member-card-${member.id}`}
              >
                {/* Left: Avatar + Details */}
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className="w-10 h-10 rounded-full bg-sky-500/15 border border-sky-400/30 flex items-center justify-center text-sky-400 font-bold text-xs shrink-0">
                    {member.picture_url ? (
                      <img
                        src={member.picture_url}
                        alt=""
                        className="w-full h-full rounded-full object-cover"
                      />
                    ) : (
                      initials
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <p className="text-xs font-bold text-white truncate">
                        {member.full_name || "TA Member"}
                      </p>
                      {member.role === "admin" && (
                        <span className="px-1.5 py-0.2 rounded-full bg-purple-500/20 text-purple-300 text-[9px] font-bold">
                          Admin
                        </span>
                      )}
                      {member.role === "trainer" && (
                        <span className="px-1.5 py-0.2 rounded-full bg-emerald-500/20 text-emerald-300 text-[9px] font-bold">
                          Trainer
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] text-slate-400 truncate">{member.email}</p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <span
                        className={`text-[9px] font-bold px-2 py-0.5 rounded-full ${tierConfig.className}`}
                      >
                        {formatMembership(member.membership_type)}
                      </span>
                      {member.membership_expires && (
                        <span
                          className={`text-[10px] ${
                            isExpired ? "text-red-400 font-bold" : "text-slate-400"
                          }`}
                        >
                          {isExpired ? "Expired" : "Expires"}: {member.membership_expires}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Right: Quick Edit Action */}
                <button
                  type="button"
                  onClick={() => setEditingUser(member)}
                  className="p-2 rounded-xl bg-white/5 hover:bg-sky-500/20 text-slate-300 hover:text-sky-300 border border-white/10 active:scale-95 transition-all shrink-0 cursor-pointer"
                  aria-label="Edit Member"
                  data-testid={`edit-member-${member.id}`}
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Dialogs */}
      {showInvite && (
        <InviteUserDialog
          onClose={() => setShowInvite(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ["admin_members"] });
          }}
        />
      )}

      {editingUser && (
        <EditUserDialog
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSave={async (patch) => {
            await base44.entities.User.update(editingUser.id, patch);
            queryClient.invalidateQueries({ queryKey: ["admin_members"] });
            setEditingUser(null);
          }}
        />
      )}
    </div>
  );
}
